import React, { useCallback, useEffect, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToggle } from 'Hooks/useToggle'
import { Accordion } from 'Components/shared/accordion'
import { SidebarFieldWrapper } from 'Components/shared/sidebar'

import { Slider } from 'Components/shared/slider'
import { SidebarField } from 'Components/shared/sidebar'
import { Gpa as GpaComponent } from 'Components/shared/segmentfilters/prospectprofile/Gpa'
import { selectGpaTestScores } from './GpaTestScoresSlice'
import { GpaTestScores } from './GpaTestScores'

import {
  selectIntendedAreaofStudy,
  setOnDemandIntendedAreaofStudy,
} from './IntendedAreaofStudySlice'

import {
  selectAreaOfInterest,
  setOnDemandAreaOfInterest,
  setAreaOfInterestDefaultValue,
} from './AreaOfInterestSlice'

import {
  selectAcademicDiscipline,
  setOnDemandAcademicDiscipline,
} from './AcademicDisciplineSlice'

import {
  selectExpectedTransferTerm,
  setOnDemandExpectedTransferTerm,
} from './ExpectedTransferTermSlice'
import {
  selectGraduationYear,
  setGraduationYearDefaultValue,
  setOnDemandGraduationYearRange,
} from './GraduationYearSlice'

import {
  selectLastDegreeCompleted,
  setLastDegreeCompleted,
} from './LastDegreeCompletedSlice'

import {
  selectProfessionalCredential,
  setProfessionalCredential,
} from './ProfessionalCredentialSlice'

import {
  selectAthletics,
  setAthletics,
} from './AthleticsSlice'

import {
  selectExtracurriculars,
  setExtracurriculars,
} from './ExtracurricularsSlice'

import {
  selectCertificateSeeking,
  setCertificateSeeking,
} from './CertificateSeekingSlice'

import {
  selectInstructionalLevel,
  setInstructionalLevel,
} from './InstructionalLevelSlice'

import {
  selectStartTimeframe,
  setStartTimeframe,
} from './StartTimeframeSlice'

import {
  selectCurrentlyEnrolled,
  setCurrentlyEnrolled,
} from './CurrentlyEnrolledSlice'
import {
  selectLevelOfDegreeSeeking,
  setLevelOfDegreeSeekingDefaultValue,
  setLevelOfDegreeSeeking,
} from './LevelOfDegreeSeekingSlice'

import {
  selectStudyAbroad,
  setStudyAbroad,
} from 'Slices/segments/StudyAbroad'

import {
  selectReligiousInstitution,
  setReligiousInstitution,
} from 'Slices/segments/ReligiousInstitution'

import {
  selectUTMMedium,
  setUTMMedium,
} from './UTMMediumSlice'

import {
  selectUTMSource,
  setUTMSource,
} from './UTMSourceSlice'

import {
  selectUTMCampaign,
  setUTMCampaign,
} from './UTMCampaignSlice'

import {
  selectMilitaryPreferences,
  setMilitaryPreference,
} from './MilitaryPreferenceSlice'

import {
  selectTCPAConsent,
  setTCPAConsent,
} from './TCPAConsentSlice'

import {
  selectPhoneTCPAConsent,
  setPhoneTCPAConsent,
} from './PhoneTCPAConsentSlice'

import {
  selectIntendedMajors,
  setIntendedMajors
} from './IntendedMajorSlice'



import { selectGpa, setOnDemandAnyGpa, setOnDemandGpaRange } from './GpaSlice'
import { FilterSingleSelectDropdown } from 'Components/shared/singleselectdropdown'
import { VirtualizedMultiSelect } from 'Components/shared/multiselectdropdown'
import { Contract } from 'Services/contracts/contractsUtils'
import { setOnDemandModality, selectModality } from './ModalitySlice'
import {
  setOnDemandExperienceRange,
  setOnDemandExperienceRangeSelection,
  selectExperience,
  setExperienceDefaultValue,
} from './YearsOfWorkExperienceSlice'

import { useGetUTMMediumQuery, useGetUTMSourceQuery, useGetUTMCampaignQuery } from 'Services/contracts/candidatesContracts'

interface ProspectProfileProps {
  chosenItem: Partial<Contract>
  filterData?: any
  segmentId?: string
  resetKey?: number
}

export const ProspectProfile = memo(
  ({ chosenItem, filterData, segmentId, resetKey }: ProspectProfileProps) => {
    const dispatch = useDispatch()
    const [expand, setExpand] = useToggle(false)

    const gpa = useSelector(selectGpa)
    const gpaTestScores = useSelector(selectGpaTestScores)
    const levelOfDegreeSeeking = useSelector(selectLevelOfDegreeSeeking)
    const intendedAreaofStudy = useSelector(selectIntendedAreaofStudy)
    const areaOfInterest = useSelector(selectAreaOfInterest)
    const academicDiscipline = useSelector(selectAcademicDiscipline)
    const expectedTransferTerm = useSelector(selectExpectedTransferTerm)
    const lastDegreeCompleted = useSelector(selectLastDegreeCompleted)
    const currentlyEnrolled = useSelector(selectCurrentlyEnrolled)
    const graduationYear = useSelector(selectGraduationYear)
    const modality = useSelector(selectModality)
    const experience = useSelector(selectExperience)
    const studyAbroad = useSelector(selectStudyAbroad)
    const religiousInstitution = useSelector(selectReligiousInstitution)
    const professionalCredential = useSelector(selectProfessionalCredential)
    const athletics = useSelector(selectAthletics)
    const extracurriculars = useSelector(selectExtracurriculars)
    const certificateSeeking = useSelector(selectCertificateSeeking)
    const instructionalLevel = useSelector(selectInstructionalLevel)
    const startTimeframe = useSelector(selectStartTimeframe)
    const UTMMedium = useSelector(selectUTMMedium)
    const UTMSource = useSelector(selectUTMSource)
    const UTMCampaign = useSelector(selectUTMCampaign)
    const militaryPreferences = useSelector(selectMilitaryPreferences)
    const TCPAConsent = useSelector(selectTCPAConsent)
    const phoneTCPAConsent = useSelector(selectPhoneTCPAConsent)
    const intendedMajors = useSelector(selectIntendedMajors)

    const { data: utmMediumData } = useGetUTMMediumQuery()
    const { data: utmSourceData } = useGetUTMSourceQuery()
    const { data: utmCampaignData } = useGetUTMCampaignQuery()

    /** Reset Some Defaults */
    useEffect(() => {
        const audienceType = chosenItem?.audience_type
        setLevelOfDegreeSeekingDefaultValue(dispatch, audienceType)
        setGraduationYearDefaultValue(dispatch, audienceType)
        setAreaOfInterestDefaultValue(dispatch, audienceType)
        // setExperienceDefaultValue(dispatch, audienceType) #TODO: To be added back next sprint
        dispatch(setOnDemandAnyGpa({ audienceType: chosenItem?.audience_type, selections: ['Any']}))
    }, [chosenItem?.id, resetKey])

    /** This useEffect is for setting the filters in edit segment */
    useEffect(() => {
      if (segmentId && filterData) {
        if (filterData?.[modality.label]) {
          dispatch(setOnDemandModality(filterData[modality.label]))
        }

        if (filterData?.[experience.label]) {
          let selections = filterData?.[experience.label]
          dispatch(setOnDemandExperienceRangeSelection({selections}))
        }

        if (filterData?.[levelOfDegreeSeeking.label]) {
          dispatch(
            setLevelOfDegreeSeeking(filterData[levelOfDegreeSeeking.label])
          )
        }

        if (filterData?.[intendedAreaofStudy.label])
          dispatch(
            setOnDemandIntendedAreaofStudy(
              filterData[intendedAreaofStudy.label]
            )
          )
        if (filterData?.[areaOfInterest.label])
          dispatch(setOnDemandAreaOfInterest(filterData[areaOfInterest.label]))

        if (filterData?.[expectedTransferTerm.label])
          dispatch(
            setOnDemandExpectedTransferTerm(
              filterData[expectedTransferTerm.label]
            )
          )

        if (filterData?.[lastDegreeCompleted.label]) {
          dispatch(
            setLastDegreeCompleted(filterData?.[lastDegreeCompleted.label])
          )
        }
        if (filterData?.[professionalCredential.label]) {
          dispatch(setProfessionalCredential(filterData?.[professionalCredential.label]))
        }
        if (filterData?.[athletics.label]) {
          dispatch(setAthletics(filterData?.[athletics.label]))
        }
        if (filterData?.[extracurriculars.label]) {
          dispatch(setExtracurriculars(filterData?.[extracurriculars.label]))
        }
        if (filterData?.[certificateSeeking.label]) {
          dispatch(setCertificateSeeking(filterData?.[certificateSeeking.label]))
        }
        if (filterData?.[instructionalLevel.label]) {
          dispatch(setInstructionalLevel(filterData?.[instructionalLevel.label]))
        }
        if (filterData?.[startTimeframe.label]) {
          dispatch(setStartTimeframe(filterData?.[startTimeframe.label]))
        }
        if(filterData?.[UTMMedium.label]) {
          dispatch(setUTMMedium(filterData[UTMMedium.label]))
        }
        if(filterData?.[UTMSource.label]) {
          dispatch(setUTMSource(filterData[UTMSource.label]))
        }
        if(filterData?.[UTMCampaign.label]) {
          dispatch(setUTMCampaign(filterData[UTMCampaign.label]))
        }
        if (filterData?.[currentlyEnrolled.label]) {
          dispatch(setCurrentlyEnrolled(filterData?.[currentlyEnrolled.label]))
        }

        if (filterData?.[graduationYear.label]) {
          const range = filterData?.[graduationYear.label]
          dispatch(
            setOnDemandGraduationYearRange({
              min: range[0],
              max: range[1],
            })
          )
        }

        if (filterData?.[gpa.label]) {
          const selections = filterData?.[gpa.label]
            dispatch(
              setOnDemandAnyGpa({
                audienceType: chosenItem?.audience_type,
                selections
              })
            )
        }

        if (filterData?.[academicDiscipline.label]) {
          dispatch(
            setOnDemandAcademicDiscipline(
              filterData?.[academicDiscipline.label]
            )
          )
        }

        if(filterData?.[studyAbroad.label]) {
          dispatch(setStudyAbroad(filterData[studyAbroad.label]))
        }

        if(filterData?.[religiousInstitution.label]) {
          dispatch(setReligiousInstitution(filterData[religiousInstitution.label]))
        }
        if (filterData?.[militaryPreferences.label]) {
          dispatch(setMilitaryPreference(filterData[militaryPreferences.label]))
        }
        if (filterData?.[TCPAConsent.label]) {
          dispatch(setTCPAConsent(filterData[TCPAConsent.label]))
        }
        if(filterData?.[phoneTCPAConsent.label]) {
          dispatch(setPhoneTCPAConsent(filterData[phoneTCPAConsent.label]))
        }
        if(filterData?.[intendedMajors.label]) {
          dispatch(setIntendedMajors(filterData[intendedMajors.label]))
        }
      }
    }, [filterData])

    const dispatchModality = useCallback((value: string[]) => {
      dispatch(setOnDemandModality(value))
    }, [])

    const dispatchExperience = useCallback(({ min, max }) => {
      dispatch(setOnDemandExperienceRange({ min, max }))
    }, [])

    const dispatchLevelOfDegreeSeeking = useCallback((values: string[]) => {
      dispatch(setLevelOfDegreeSeeking(values))
    }, [])

    const dispatchAcademicDiscipline = useCallback((values: string[]) => {
      dispatch(setOnDemandAcademicDiscipline(values))
    }, [])

    const dispatchIntendedAreaofStudy = useCallback((values: string[]) => {
      dispatch(setOnDemandIntendedAreaofStudy(values))
    }, [])

    const dispatchAreaOfInterest = useCallback((values: string[]) => {
      dispatch(setOnDemandAreaOfInterest(values))
    }, [])

    const dispatchExpectedTransferTerm = useCallback((values: string[]) => {
      dispatch(setOnDemandExpectedTransferTerm(values))
    }, [])
    const dispatchCurrentlyEnrolled = useCallback((values: string[]) => {
      dispatch(setCurrentlyEnrolled(values))
    }, [])

    const dispatchGraduationYearRange = useCallback(({ min, max }) => {
      dispatch(setOnDemandGraduationYearRange({ min, max }))
    }, [])

    const dispatchLastDegreeCompleted = useCallback((values: string[]) => {
      dispatch(setLastDegreeCompleted(values))
    }, [])

    const dispatchProfessionalCredential = useCallback((values: string[]) => {
      dispatch(setProfessionalCredential(values))
    }, [])

    const dispatchAthletics = useCallback((values: string[]) => {
      dispatch(setAthletics(values))
    }, [])

    const dispatchExtracurriculars = useCallback((values: string[]) => {
      dispatch(setExtracurriculars(values))
    }, [])

    const dispatchCertificateSeeking = useCallback((values: string[]) => {
      dispatch(setCertificateSeeking(values))
    }, [])

    const dispatchInstructionalLevel = useCallback((values: string[]) => {
      dispatch(setInstructionalLevel(values))
    }, [])

    const dispatchStartTimeframe = useCallback((values: string[]) => {
      dispatch(setStartTimeframe(values))
    }, [])

    const dispatchAnyGpa = useCallback((selections) => {
      dispatch(setOnDemandAnyGpa({ audienceType: chosenItem?.audience_type, selections}))
    }, [chosenItem?.id])

    const dispatchMilitaryPreference = useCallback((values: string[]) => {
      dispatch(setMilitaryPreference(values))
    }, [])

    const dispatchTCPAConsent = useCallback((values: string[]) => {
      dispatch(setTCPAConsent(values))
    }, [])

    const dispatchPhoneTCPAConsent = useCallback((values: string[]) => {
      dispatch(setPhoneTCPAConsent(values))
    }, [])

    const dispatchIntendedMajors = useCallback((values: string[]) => {
      dispatch(setIntendedMajors(values))
    }, [])

    const dispatchGpaRange = useCallback(
      ({ min, max }) => {
        dispatch(
          setOnDemandGpaRange({
            min,
            max,
            audienceType: chosenItem?.audience_type,
          })
        )
      },
      [chosenItem]
    )

    return (
      <Accordion
        setExpand={setExpand}
        expand={expand}
        title='PROSPECT PROFILE'
        disabled={chosenItem?.id ? false : true}
      >
        <SidebarFieldWrapper>
          {modality.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={modality.label}
              counterLabel={'Modality'}
              values={modality.values}
              initialSelectedValues={modality.selectedValues}
              selectedValues={modality.selectedValues}
              setSelectedValues={dispatchModality}
            />
          )}
          {/* #TODO To be added back next sprint */}
          {experience.audienceTypes.includes(chosenItem?.audience_type) && (
            <>
            <VirtualizedMultiSelect
              label={experience.label}
              counterLabel="Selections"
              values={experience.values}
              selectedValues={experience.selections}
              initialSelectedValues={experience.selections}
              setSelectedValues={(selections) => dispatch(setOnDemandExperienceRangeSelection({selections}))}
              />
            {experience.selections?.includes('Specific Year Range') && <SidebarField id='experience' label="Experience Range">
              <Slider
                ariaLabel={experience.ariaLabel}
                min={experience.minExperience}
                max={experience.maxExperience}
                selectedMin={experience.selectedMinExperience}
                selectedMax={experience.selectedMaxExperience}
                step={experience.step}
                handleChange={(range: number[]) =>
                  dispatchExperience({ min: range[0], max: range[1] })
                }
              />
            </SidebarField>}
            </>
          )}
          {graduationYear.audienceTypes.includes(chosenItem?.audience_type) && (
            <SidebarField id='graduation year' label={graduationYear.label}>
              <Slider
                ariaLabel={graduationYear.ariaLabel}
                min={graduationYear.minYear}
                max={graduationYear.maxYear}
                selectedMin={graduationYear.selectedMinGradYear}
                selectedMax={graduationYear.selectedMaxGradYear}
                step={graduationYear.step}
                handleChange={(range: number[]) =>
                  dispatchGraduationYearRange({ min: range[0], max: range[1] })
                }
              />
            </SidebarField>
          )}

          {expectedTransferTerm.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={expectedTransferTerm.label}
              counterLabel={'Transfer Term'}
              values={expectedTransferTerm.values}
              initialSelectedValues={expectedTransferTerm.selectedValues}
              selectedValues={expectedTransferTerm.selectedValues}
              setSelectedValues={dispatchExpectedTransferTerm}
            />
          )}

          {areaOfInterest.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={areaOfInterest.label}
              counterLabel={'Area Of Interest'}
              values={areaOfInterest.values}
              initialSelectedValues={areaOfInterest.selectedValues}
              selectedValues={areaOfInterest.selectedValues}
              setSelectedValues={dispatchAreaOfInterest}
            />
          )}
          {intendedMajors.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={intendedMajors.label}
              counterLabel={'Intended Major'}
              values={intendedMajors.values}
              initialSelectedValues={intendedMajors.selectedValues}
              selectedValues={intendedMajors.selectedValues}
              setSelectedValues={dispatchIntendedMajors}
            />
          )}
          {academicDiscipline.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={academicDiscipline.label}
              counterLabel={'Academic Discipline'}
              values={academicDiscipline.values}
              initialSelectedValues={academicDiscipline.selectedValues}
              selectedValues={academicDiscipline.selectedValues}
              setSelectedValues={dispatchAcademicDiscipline}
            />
          )}
          {intendedAreaofStudy.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={intendedAreaofStudy.label}
              counterLabel={'Area Of Study'}
              values={intendedAreaofStudy.values}
              initialSelectedValues={intendedAreaofStudy.selectedValues}
              selectedValues={intendedAreaofStudy.selectedValues}
              setSelectedValues={dispatchIntendedAreaofStudy}
            />
          )}
          {levelOfDegreeSeeking.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={levelOfDegreeSeeking.label}
              counterLabel={'Degree'}
              values={levelOfDegreeSeeking.values}
              selectedValues={levelOfDegreeSeeking.selectedValues}
              initialSelectedValues={levelOfDegreeSeeking.selectedValues}
              setSelectedValues={dispatchLevelOfDegreeSeeking}
            />
          )}
          {gpa.audienceTypes.includes(chosenItem?.audience_type) && (
            <GpaComponent
              dispatchAnyGpa={dispatchAnyGpa}
              dispatchGpaRange={dispatchGpaRange}
              gpaState={gpa}
            />
          )}

          {gpaTestScores.audienceTypes.includes(chosenItem?.audience_type) && (
            <GpaTestScores audienceType={chosenItem?.audience_type} />
          )}
          {lastDegreeCompleted.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={lastDegreeCompleted.label}
              counterLabel={'Degree'}
              values={lastDegreeCompleted.values}
              selectedValues={lastDegreeCompleted.selectedValues}
              initialSelectedValues={lastDegreeCompleted.selectedValues}
              setSelectedValues={dispatchLastDegreeCompleted}
            />
          )}
          {professionalCredential.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={professionalCredential.label}
              counterLabel={'Credential'}
              values={professionalCredential.values}
              selectedValues={professionalCredential.selectedValues}
              initialSelectedValues={professionalCredential.selectedValues}
              setSelectedValues={dispatchProfessionalCredential}
            />
          )
          }
          {certificateSeeking.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={certificateSeeking.label}
              counterLabel={'Certificate'}
              values={certificateSeeking.values}
              selectedValues={certificateSeeking.selectedValues}
              initialSelectedValues={certificateSeeking.selectedValues}
              setSelectedValues={dispatchCertificateSeeking}
            />
          )}
          {instructionalLevel.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={instructionalLevel.label}
              counterLabel={'Level'}
              values={instructionalLevel.values}
              selectedValues={instructionalLevel.selectedValues}
              initialSelectedValues={instructionalLevel.selectedValues}
              setSelectedValues={dispatchInstructionalLevel}
            />
          )}
          {startTimeframe.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={startTimeframe.label}
              counterLabel={'Timeframe'}
              values={startTimeframe.values}
              selectedValues={startTimeframe.selectedValues}
              initialSelectedValues={startTimeframe.selectedValues}
              setSelectedValues={dispatchStartTimeframe}
            />
          )}
          {UTMMedium.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={UTMMedium.label}
              counterLabel={'Selections'}
              values={UTMMedium.values}
              selectedValues={UTMMedium.selectedValues}
              initialSelectedValues={UTMMedium.selectedValues}
              setSelectedValues={(values) => dispatch(setUTMMedium(values))}
            />
          )}
          {UTMSource.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={UTMSource.label}
              counterLabel={'Selections'}
              values={UTMSource.values}
              selectedValues={UTMSource.selectedValues}
              initialSelectedValues={UTMSource.selectedValues}
              setSelectedValues={(values) => dispatch(setUTMSource(values))}
            />
          )}
          {UTMCampaign.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={'utm_campaign'}
              counterLabel={'Selections'}
              values={UTMCampaign.values}
              selectedValues={UTMCampaign.selectedValues}
              initialSelectedValues={UTMCampaign.selectedValues}
              setSelectedValues={(values) => dispatch(setUTMCampaign(values))}
            />
          )}
          {currentlyEnrolled.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={currentlyEnrolled.label}
              counterLabel={'Enrolled'}
              values={currentlyEnrolled.values}
              selectedValues={currentlyEnrolled.selectedValues}
              initialSelectedValues={currentlyEnrolled.selectedValues}
              setSelectedValues={dispatchCurrentlyEnrolled}
              />
          )}
          {studyAbroad.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={studyAbroad.label}
              counterLabel={'Study Abroad'}
              values={studyAbroad.values}
              initialSelectedValues={studyAbroad.selectedValues}
              selectedValues={studyAbroad.selectedValues}
              setSelectedValues={(values) => dispatch(setStudyAbroad(values))}
              />)
          }
          {religiousInstitution.audienceTypes.includes(chosenItem?.audience_type) && (
            <VirtualizedMultiSelect
              label={religiousInstitution.label}
              counterLabel={'Religious Institution'}
              values={religiousInstitution.values}
              initialSelectedValues={religiousInstitution.selectedValues}
              selectedValues={religiousInstitution.selectedValues}
              setSelectedValues={(values) => dispatch(setReligiousInstitution(values))}
              />)
          }
          {athletics.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={athletics.label}
              counterLabel={'Athletic'}
              values={athletics.values}
              selectedValues={athletics.selectedValues}
              initialSelectedValues={athletics.selectedValues}
              setSelectedValues={dispatchAthletics}
            />
          )}
          {extracurriculars.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={extracurriculars.label}
              counterLabel={'Extracurricular'}
              values={extracurriculars.values}
              selectedValues={extracurriculars.selectedValues}
              initialSelectedValues={extracurriculars.selectedValues}
              setSelectedValues={dispatchExtracurriculars}
            />
          )}
          {militaryPreferences.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={militaryPreferences.label}
              counterLabel={'Preference'}
              values={militaryPreferences.values}
              selectedValues={militaryPreferences.selectedValues}
              initialSelectedValues={militaryPreferences.selectedValues}
              setSelectedValues={dispatchMilitaryPreference}
            />
          )}
          {phoneTCPAConsent.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={phoneTCPAConsent.label}
              counterLabel={'Consent'}
              values={phoneTCPAConsent.values}
              selectedValues={phoneTCPAConsent.selectedValues}
              initialSelectedValues={phoneTCPAConsent.selectedValues}
              setSelectedValues={dispatchPhoneTCPAConsent}
            />
          )}
          {TCPAConsent.audienceTypes.includes(
            chosenItem?.audience_type
          ) && (
            <VirtualizedMultiSelect
              label={TCPAConsent.label}
              counterLabel={'Consent'}
              values={TCPAConsent.values}
              selectedValues={TCPAConsent.selectedValues}
              initialSelectedValues={TCPAConsent.selectedValues}
              setSelectedValues={dispatchTCPAConsent}
            />
          )}
        </SidebarFieldWrapper>
      </Accordion>
    )
  }
)
